import { useState, useEffect } from 'react';
import ActorAvatar from './ActorAvatar.jsx';
import { getActorStatusFromLastActive } from '../utils/actorStatusHelper.js';

const getApiUrl = () => {
  const RAILWAY_API = import.meta.env.VITE_API_URL || 'https://registration-app-production.up.railway.app';
  const LOCALHOST_API = 'http://localhost:3000';
  
  const isLocalhost = 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  return isLocalhost ? LOCALHOST_API : RAILWAY_API;
};
const API_URL = getApiUrl();

const AffiliationsSection = ({ actorId }) => {
  const [affiliations, setAffiliations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAffiliations = async () => {
      if (!actorId) return;

      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const [projectsRes, storesRes] = await Promise.all([
          fetch(`${API_URL}/api/project/actor/${actorId}/affiliations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_URL}/api/stores/actor/${actorId}/affiliations`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const projectsData = projectsRes.ok ? await projectsRes.json() : { projects: [] };
        const storesData = storesRes.ok ? await storesRes.json() : { stores: [] };

        const combined = [
          ...(projectsData.projects || []),
          ...(storesData.stores || []),
        ];

        setAffiliations(combined);
      } catch (error) {
        console.error('Error fetching affiliations:', error);
        setAffiliations([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAffiliations();
  }, [actorId]);

  if (loading) {
    return (
      <div className="mb-6">
        <p className="text-gray-500 text-sm">読み込み中...</p>
      </div>
    );
  }

  if (affiliations.length === 0) {
    return (
      <div className="mb-6">
        <p className="text-gray-500 text-sm text-center py-4">所属組織はありません</p>
      </div>
    );
  }

  return (
    <div className="mb-6">
      <div className="bg-gray-50 rounded-lg overflow-hidden">
        <table className="w-full text-xs">
          <thead className="bg-gray-200">
            <tr>
              <th className="px-1 py-2 text-left font-semibold text-gray-700 text-[10px]">オーナー</th>
              <th className="px-1 py-2 text-left font-semibold text-gray-700 text-[10px]">組織名</th>
              <th className="px-1 py-2 text-left font-semibold text-gray-700 text-[10px]">タイプ</th>
              <th className="px-1 py-2 text-left font-semibold text-gray-700 text-[10px]">役割</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {affiliations.map((affiliation, index) => (
              <tr key={`${affiliation.type}-${affiliation.id}-${index}`} className="hover:bg-gray-100">
                <td className="px-1 py-2">
                  <div className="flex items-center gap-1">
                    <ActorAvatar
                      actorId={affiliation.owner?.actor_id}
                      displayName={affiliation.owner?.display_name}
                      profileImageUrl={affiliation.owner?.profile_image_url}
                      actorStatus={getActorStatusFromLastActive(affiliation.owner?.last_active_at)}
                      actorType={affiliation.owner?.type || 'user'}
                      size="sm"
                      noPadding
                    />
                    <span className="text-gray-800 text-[10px] leading-tight break-words">
                      {affiliation.owner?.display_name}
                    </span>
                  </div>
                </td>
                <td className="px-1 py-2">
                  <span className="text-gray-800 text-[10px] leading-tight break-words block">
                    {affiliation.name}
                  </span>
                </td>
                <td className="px-1 py-2">
                  <span className={`px-1 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${
                    affiliation.type === 'store' 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {affiliation.type === 'store' ? 'ストア' : 'PJ'}
                  </span>
                </td>
                <td className="px-1 py-2">
                  <span className={`px-1 py-0.5 rounded-full text-[9px] font-medium whitespace-nowrap ${
                    affiliation.role === 'owner' 
                      ? 'bg-purple-100 text-purple-800' 
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {affiliation.role === 'owner' ? 'オーナー' : 'メンバー'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AffiliationsSection;
